import * as Yup from 'yup';
import { App } from '../../App';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import { ButtonActor, SelectActor, Text } from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getFetchHeaders } from '../../utils/fetchHeaders';
import { getScreenTranslation } from '../../utils/translations';
import { InformationIcon } from 'components/icons';
import { injectJuicyScript } from '../../utils/injectScript';
import { JUICY_NEXT_BTN } from 'utils/constants';
import { routes } from '../../routes';
import { sourceOfIncome } from './sourceOfIncome';
import { useAnalytics } from 'use-analytics';
import { useCallback, useEffect, useState } from 'react';
import { useErrorManager } from 'InternalLibrary';
import { useFormik } from 'formik';
import {
  useGetClientApplication,
  usePutApplication,
  usePutProfile,
} from '../../api';
import { useJuicy } from '../../hooks/useJuicy';
import { useMaybeCreateApplication } from '../../hooks/useMaybeCreateApplication';
import { useNavigate } from 'react-router-dom';
import useClient from '../../hooks/useClient';

import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useWebcode } from '../../hooks/useWebcode';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import Spacer from 'components/atoms/Spacer/Spacer';
import styles from './IncomeDetails.module.scss';
import Title from 'components/atoms/Title/Title';
import useIovation, { ioBlackBoxAtom } from '../../hooks/useIovation';

export default function IncomeDetails() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setError } = useErrorManager();
  const { fetch: fetchPutProfile } = usePutProfile();
  const { fetchClient } = useClient();
  const { fetch: fetchPutApplication } = usePutApplication();
  const { data: applicationData, fetch: fetchGetApplication } =
    useGetClientApplication();
  const { maybeCreateApplication } = useMaybeCreateApplication();
  const { isWebCodeRequired } = useWebcode();
  const { track } = useAnalytics();
  const { generateJuicyApplicationSession } = useJuicy();
  useIovation();
  const i18Next = useTranslation('incomeDetails');
  const ioBlackBox = useRecoilValue(ioBlackBoxAtom);
  const apiAffiliateEventQualified = App.useApi(
    'affiliate-events/qualified',
    'POST',
  );

  const validationSchema = Yup.object().shape({
    incomeSource: Yup.string().required(i18Next.t('validation:required_field')),
    averageMonthlyIncome: Yup.string().required(
      i18Next.t('validation:required_field'),
    ),
  });

  const sourceOfIncomeOptions = sourceOfIncome.map(({ code, value }) => ({
    name: i18Next.t(`employment_status.${code}`),
    value,
  }));

  useEffect(() => {
    injectJuicyScript();
  }, []);

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        formik.setStatus({ errors: {} });
        setLoading(true);

        await fetchPutProfile({
          body: {
            employmentStatus: data.incomeSource,
            monthlyIncome: data.averageMonthlyIncome,
            registrationPhase: 'INCOME',
          },
        });

        // Update apiGetClient data
        const client = await fetchClient();
        const isWebcodeNeeded = await isWebCodeRequired();

        const nextRoute = isWebcodeNeeded
          ? routes.FirstLoan_PhoneVerification
          : routes.FirstLoan_OnlineBank_Verify;

        await maybeCreateApplication();

        track('identify', { client, email: client?.email?.email });

        // Update apiGetApplicaiton data
        const application =
          applicationData ||
          (await fetchGetApplication({
            headers: getFetchHeaders({
              Accept: 'application/vnd.4finance.web.v1.hal+json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            }),
          }));

        track('identify', { application });

        if (application?.resolution === 'REJECTED') {
          try {
            await generateJuicyApplicationSession({
              nextRoute: routes.FirstLoan_Rejected,
            });
          } catch {}
          navigate(routes.FirstLoan_Rejected);
          return;
        }

        // Webcode already not required, confirm application and continue to identification process
        if (!isWebcodeNeeded) {
          await fetchPutApplication({
            body: {
              ioBlackBox,
            },
          });
          await fetchGetApplication();

          if (client?.isRegisteredByAffiliate) {
            await apiAffiliateEventQualified.fetch({
              headers: getFetchHeaders(),
            });
          }

          await generateJuicyApplicationSession({
            nextRoute,
          });
          return;
        }
        navigate(nextRoute);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    [
      fetchPutProfile,
      maybeCreateApplication,
      isWebCodeRequired,
      navigate,
      navigate,
      setError,
    ],
  );

  const formik = useFormik({
    initialValues: {
      incomeSource: '',
      averageMonthlyIncome: '',
    },
    validationSchema,
    onSubmit: async (data) => {
      formik.setStatus({ errors: {} });
      setLoading(true);
      await handleSubmit(data);
      setLoading(false);
    },
  });

  const content = getScreenTranslation('incomeDetails', i18Next);

  return (
    <Frame
      {...frameConfig}
      sectionID={AppRegistrationSection.Confirmation}
      bodyWrapperClassName={styles.frame}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className={layout['width-sm']}>
          <Spacer marginBottom={{ default: '54px', s: '40px' }}>
            <Title text={content.title} data-test="css-Income-Details" />
          </Spacer>

          <Spacer marginBottom="20px">
            <SelectActor
              className={styles.border}
              name="incomeSource"
              label={content.incomeSourcePlaceholder}
              selectProps={{
                options: sourceOfIncomeOptions,
                dataTest: 'incomeSourceSelect',
              }}
              formik={formik}
              data-test="css-Income-Source"
              analytics={{
                description: 'Source of income',
              }}
            />
          </Spacer>

          <Spacer marginBottom={{ default: '55px', s: '42px' }}>
            <SelectActor
              className={styles.border}
              name="averageMonthlyIncome"
              label={content.avgIncomeSourcePlaceholder}
              selectProps={{
                options: [
                  { value: '00', name: '< 400 €' },
                  { value: '450', name: '400 – 499 €' },
                  { value: '600', name: '500 – 699 €' },
                  { value: '800', name: '700 – 899 €' },
                  { value: '1000', name: '900 – 1099 €' },
                  { value: '1200', name: '1100 – 1399 €' },
                  { value: '1600', name: '> 1400 €' },
                ],
                dataTest: 'incomAmountSelect',
              }}
              formik={formik}
              data-test="css-Income-Source"
              analytics={{
                description: 'Average monthly income',
              }}
            />

            <Spacer marginTop="15px">
              <span className={styles['income-details-icon']}>
                {<InformationIcon data-test="incomeDetails-icon-info" />}
              </span>
              <Text
                level={3}
                text={content.description}
                dataTest="description"
              />
            </Spacer>
          </Spacer>

          <Spacer marginBottom={{ s: '40px' }}>
            <ButtonActor
              buttonProps={{
                className: classes(styles['continue-button'], JUICY_NEXT_BTN),
                type: 'submit',
                loading,
                dataTest: 'css-Continue-Button',
              }}
              formik={formik}
              analytics={{
                description: 'Continue button',
              }}
            >
              {content.continueButtonText}
            </ButtonActor>
          </Spacer>
        </div>
      </form>
    </Frame>
  );
}
